import { AxiosError } from 'axios';
import { betterTypeOf } from './betterTypeOf';

interface ArgumentErrors {
  [k: string]: string[];
}

function isObjectOfStringArrays(o: unknown): o is ArgumentErrors {
  for (const val of Object.values(o)) {
    if (!Array.isArray(val)) {
      return false;
    }

    for (const entry of val) {
      if (typeof entry !== 'string') {
        return false;
      }
    }
  }

  return true;
}

function formatError(o: ArgumentErrors): string {
  let errorMsg = '';

  for (const prop in o) {
    const p = prop.replace('_', ' ');
    let currentMsg = p.replace(/\b\w/g, (l) => l.toUpperCase());
    for (const error of o[prop]) {
      currentMsg += ': ' + capitaliseString(error);
    }
    errorMsg += currentMsg + '\n';
  }

  return errorMsg;
}

export function getErrorMessage(responseData: unknown): string {
  if (typeof responseData === 'object' && responseData !== null) {
    if (isObjectOfStringArrays(responseData)) {
      return formatError(responseData);
    }
    if ((responseData as { detail: string })?.detail) {
      return capitaliseString(responseData['detail']);
    }
  }

  return 'Unknown error';
}

export const capitaliseString = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const stringifyObject = (
  object: unknown,
  indent = 2,
  initIndent = 0
): string => {
  const type = betterTypeOf(object);

  if (type === 'date') return `${(object as Date).toISOString()}`;
  if (type === 'boolean') return `${object ? 'true' : 'false'}`;
  if (type === 'function') return `<Function>`;
  if (type === 'null') return `null`;
  if (type === 'undefined') return `undefined`;
  if (type === 'array') {
    return (object as unknown[])
      .map(
        (item) =>
          `${' '.repeat(initIndent)}` +
          `${stringifyObject(item, indent, initIndent + indent)}`
      )
      .join(' \n');
  }
  if (type === 'object') {
    return Object.entries(object as { [key: string]: unknown })
      .map(
        ([key, item]) =>
          `${' '.repeat(initIndent)}${key}: ` +
          `${['object', 'array'].includes(betterTypeOf(item)) ? '\n' : ''}` +
          `${stringifyObject(item, indent, initIndent + indent)}`
      )
      .join('\n');
  }

  return `${object}`;
};

export const isAxiosError = <T extends unknown = unknown>(
  error: Error | AxiosError<T>
  // eslint-disable-next-line
  // @ts-ignore
): error is AxiosError<T> => error?.isAxiosError;

export const getRequestError = (
  error: Error | AxiosError
): string | undefined => {
  if (!error) return undefined;
  if (isAxiosError(error)) {
    if (error.response)
      return (
        `HTTP ${error.response.status} error` +
        `${
          error.response.data
            ? `: \n\n${stringifyObject(error.response.data)}`
            : '.'
        }`
      );
    if (error.request) return `Request error: ${error.message}`;
  }

  return error.message;
};

export interface BaseErrorAction {
  type: string;
}

export const requestErrorAction = <B extends BaseErrorAction>(
  action: B,
  _error?: Error | AxiosError,
  messagePrefix?: string
) => {
  const error = `${`${messagePrefix} \n\n` || ''}${getRequestError(_error)}`;
  window &&
    window?.deployment?.environment !== 'production' &&
    console.error(error);
  return {
    ...action,
    error,
  };
};
